export const LegislativeActionTypes = ['main', 'subsidiary', 'privileged'];
/**
 * Canonical list of `{{variable}}` placeholders supported in `LegislativeAction`
 * template fields (recommended action, motion-made, pass/fail). This drives the
 * "Available variables" card in the item-action admin form and the
 * {@link LegislativeActionTemplateVariableValues} value map.
 *
 * IMPORTANT: adding/removing a key here requires updating every render path that
 * builds a full value map for these variables. See
 * `.cursor/rules/legislative-action-template-variables.mdc` for the complete
 * checklist of files to keep in sync.
 */
export const LEGISLATIVE_ACTION_TEMPLATE_VARIABLES = [
    { key: 'continuation_date', description: 'Date of continuation, if applicable' },
    { key: 'fail_status', description: 'Legislative status if the motion fails' },
    { key: 'item_fiscal_note', description: 'Fiscal impact / fiscal note of the legislative item' },
    { key: 'item_number', description: 'Legislative item number' },
    { key: 'item_recommended_action_text', description: 'Recommended action text for the item' },
    { key: 'item_short_title', description: 'Short title of the legislative item' },
    { key: 'item_summary', description: 'Summary of the legislative item' },
    { key: 'item_title', description: 'Title of the legislative item' },
    { key: 'meeting_body', description: 'Name of the meeting body' },
    { key: 'meeting_date', description: 'Date of the meeting' },
    { key: 'motion_name', description: 'Title or name of the motion' },
    {
        key: 'mover',
        description: 'Position and last name of the member who made the motion (Position LastName)',
    },
    { key: 'mover_first_name', description: 'First name of the member who made the motion' },
    { key: 'mover_last_name', description: 'Last name of the member who made the motion' },
    { key: 'mover_position', description: 'Position of the member who made the motion' },
    { key: 'pass_status', description: 'Legislative status if the motion passes' },
    { key: 'referral_target', description: 'Body or destination for referral' },
    { key: 'result_status', description: 'Resulting legislative status for the motion' },
    {
        key: 'seconder',
        description: 'Position and last name of the member who seconded the motion (Position LastName)',
    },
    { key: 'seconder_first_name', description: 'First name of the member who seconded the motion' },
    { key: 'seconder_last_name', description: 'Last name of the member who seconded the motion' },
    { key: 'seconder_position', description: 'Position of the member who seconded the motion' },
    { key: 'vote_detail', description: 'Detailed vote breakdown' },
    { key: 'vote_summary', description: 'Summary of the vote outcome' },
];
/**
 * Checks whether a string is a supported legislative action template placeholder key.
 */
export function isLegislativeActionTemplateVariableKey(key) {
    return LEGISLATIVE_ACTION_TEMPLATE_VARIABLES.some((variable) => variable.key === key);
}
/**
 * Variable keys whose values contain rich HTML and should be inserted without
 * escaping. Pass this list via `rawHtmlKeys` when calling `renderHtmlTemplate`.
 */
export const LEGISLATIVE_ACTION_TEMPLATE_RAW_HTML_VARIABLE_KEYS = ['item_summary', 'item_recommended_action_text'];
/** Display labels for {@link LegislativeActionType} values (UI, reports, exports). */
export const LegislativeActionTypeLabel = {
    main: 'Main',
    subsidiary: 'Subsidiary',
    privileged: 'Privileged',
};
export const TiebreakRules = ['fail', 'pass'];
/** Display labels for {@link TiebreakRule} values (UI, reports, exports). */
export const tiebreakRuleLabels = {
    fail: 'Fail',
    pass: 'Pass',
};
const legislativeActions = [
    // Main
    {
        name: 'Accept',
        code: 'ACCEPT',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To accept',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'ACCEPTED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Adopt',
        code: 'ADOPT',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To adopt',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'ADOPTED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Approve',
        code: 'APPROVE',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To approve',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'APPROVED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Authorize',
        code: 'AUTHORIZE',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To authorize',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'AUTHORIZED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Award',
        code: 'AWARD',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To award',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'AWARDED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Confirm',
        code: 'CONFIRM',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To confirm',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'CONFIRMED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Direct',
        code: 'DIRECT',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To direct',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'DIRECTED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Hear presentation',
        code: 'HEAR_PRESENTATION',
        type: 'main',
        isSecondRequired: false,
        isVoteRequired: false,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To hear the presentation',
        motionMadeTemplate: null,
        passTemplate: null,
        failTemplate: null,
        passLegislativeStatusCode: 'PRESENTATION_HEARD',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Introduce Item on the Floor',
        code: 'INTRODUCE_ITEM_ON_THE_FLOOR',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To introduce the item on the floor',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'INTRODUCED',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Ratify',
        code: 'RATIFY',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To ratify',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item {{pass_status}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item {{fail_status}}.',
        passLegislativeStatusCode: 'RATIFIED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Receive and File',
        code: 'RECEIVE_AND_FILE',
        type: 'main',
        isSecondRequired: false,
        isVoteRequired: false,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To receive and file',
        motionMadeTemplate: null,
        passTemplate: null,
        failTemplate: null,
        passLegislativeStatusCode: 'RECEIVED_AND_FILED',
        failLegislativeStatusCode: 'FAILED',
    },
    {
        name: 'Reconsider',
        code: 'RECONSIDER',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To reconsider',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}.',
        failTemplate: 'Motion failed {{vote_summary}}.',
        passLegislativeStatusCode: 'RECONSIDERED',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Rescind',
        code: 'RESCIND',
        type: 'main',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To rescind',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}.',
        failTemplate: 'Motion failed {{vote_summary}}.',
        passLegislativeStatusCode: 'RESCINDED',
        failLegislativeStatusCode: null,
    },
    // Privileged
    {
        name: 'Adjourn',
        code: 'ADJOURN',
        type: 'privileged',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To adjourn',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}.',
        failTemplate: 'Motion failed {{vote_summary}}.',
        passLegislativeStatusCode: null,
        failLegislativeStatusCode: null,
    },
    {
        name: 'Call for Orders of the Day',
        code: 'CALL_FOR_ORDERS_OF_THE_DAY',
        type: 'privileged',
        isSecondRequired: false,
        isVoteRequired: false,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To call for orders of the day.',
        motionMadeTemplate: null,
        passTemplate: null,
        failTemplate: null,
        passLegislativeStatusCode: null,
        failLegislativeStatusCode: null,
    },
    {
        name: 'Point of Order',
        code: 'POINT_OF_ORDER',
        type: 'privileged',
        isSecondRequired: false,
        isVoteRequired: false,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To raise a point of order',
        motionMadeTemplate: null,
        passTemplate: null,
        failTemplate: null,
        passLegislativeStatusCode: null,
        failLegislativeStatusCode: null,
    },
    {
        name: 'Point of Privilege',
        code: 'POINT_OF_PRIVILEGE',
        type: 'privileged',
        isSecondRequired: false,
        isVoteRequired: false,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To raise a point of privilege',
        motionMadeTemplate: null,
        passTemplate: null,
        failTemplate: null,
        passLegislativeStatusCode: null,
        failLegislativeStatusCode: null,
    },
    {
        name: 'Recess',
        code: 'RECESS',
        type: 'privileged',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To recess',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}.',
        failTemplate: 'Motion failed {{vote_summary}}.',
        passLegislativeStatusCode: null,
        failLegislativeStatusCode: null,
    },
    // Subsidiary
    {
        name: 'Amend the Motion',
        code: 'AMEND_THE_MOTION',
        type: 'subsidiary',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To amend the motion',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}.',
        failTemplate: 'Motion failed {{vote_summary}}.',
        passLegislativeStatusCode: 'AMENDED',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Continue',
        code: 'CONTINUE',
        type: 'subsidiary',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To continue',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}.',
        failTemplate: 'Motion failed {{vote_summary}}.',
        passLegislativeStatusCode: 'CONTINUED',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Postpone Indefinitely',
        code: 'POSTPONE_INDEFINITE',
        type: 'subsidiary',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To postpone indefinitely',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}.',
        failTemplate: 'Motion failed {{vote_summary}}.',
        passLegislativeStatusCode: 'POSTPONED_INDEFINITELY',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Refer to Committee',
        code: 'REFER_TO_COMMITTEE',
        type: 'subsidiary',
        isSecondRequired: true,
        requiresMeetingBodyReassignment: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To refer to committee',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}.',
        failTemplate: 'Motion failed {{vote_summary}}.',
        passLegislativeStatusCode: 'REFERRED_TO_COMMITTEE',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Refer to Staff',
        code: 'REFER_TO_STAFF',
        type: 'subsidiary',
        isSecondRequired: true,
        isVoteRequired: true,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To refer to staff',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: 'Motion carried {{vote_summary}}. Item referred to {{referral_target}}.',
        failTemplate: 'Motion failed {{vote_summary}}. Item returned to prior status.',
        passLegislativeStatusCode: 'REFERRED_TO_STAFF',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Table',
        code: 'TABLE',
        type: 'subsidiary',
        isSecondRequired: true,
        isVoteRequired: false,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To table',
        motionMadeTemplate: 'Moved by {{mover}}. Seconded by {{seconder}}.',
        passTemplate: null,
        failTemplate: null,
        passLegislativeStatusCode: 'TABLED',
        failLegislativeStatusCode: null,
    },
    {
        name: 'Withdraw',
        code: 'WITHDRAW',
        type: 'subsidiary',
        isSecondRequired: false,
        isVoteRequired: false,
        isRollCallRequired: false,
        isAbstentionAllowed: true,
        recommendedActionTemplate: 'To withdraw',
        motionMadeTemplate: null,
        passTemplate: null,
        failTemplate: null,
        passLegislativeStatusCode: 'WITHDRAWN',
        failLegislativeStatusCode: null,
    },
];
export const DEFAULT_LEGISLATIVE_ACTIONS = legislativeActions.map((status, index) => ({
    ...status,
    orderById: index + 1,
    isActive: true,
}));
